import React, { useMemo, useState } from "react";
import {
  useGetSiteLayoutQuery,
  SiteLayoutData,
} from "../management-dashboard/apiSlice";
import { useDeleteLayoutMutation } from "../projects/apiSlice";
import { CoordinateTransformer } from "./coordinateTransformer";
import {
  CanvasLayout,
  Coordinate,
  DevicePin,
  RealWorldCoordinate,
} from "./types";
import SiteLayoutCanvas from "./components/SiteLayoutCanvas";
import OverlayCanvas from "./components/OverlayCanvas";
import AddLayoutWizardModal from "./components/AddLayoutWizardModal";
import DevicePlacementModal from "./components/DevicePlacementModal";

const apiBase = process.env.REACT_APP_API_URL || "/";

interface SiteLayoutManagerProps {
  mode: "edit" | "view";
  projectId: number;
}

function toCanvasLayouts(data?: SiteLayoutData): CanvasLayout[] {
  if (!data) return [];
  return data.layouts
    .filter((l) => !!l.imageFilename)
    .map((l) => ({
      id: l.id,
      name: l.name || "Layout",
      imageUrl: `${apiBase}files/${l.imageFilename}`,
      calibrationPoints: (l.calibrationPoints || []).map((cp) => ({
        imageCoordinate: { x: cp.image_x, y: cp.image_y },
        realWorldCoordinate: {
          northing: cp.real_northing,
          easting: cp.real_easting,
        },
      })),
      isCalibrated: (l.calibrationPoints || []).length >= 2,
    }));
}

function toDevicePins(data?: SiteLayoutData): DevicePin[] {
  if (!data) return [];
  return data.devices
    .filter((d) => d.coordinates)
    .map((d) => ({
      id: d.id,
      name: d.name,
      type: d.type,
      realWorld: {
        northing: d.coordinates.northing,
        easting: d.coordinates.easting,
      },
      alertStatus: d.alertStatus === "normal" ? undefined : d.alertStatus,
      recentValue: d.recentReading?.value ?? null,
    }));
}

const SiteLayoutManager: React.FC<SiteLayoutManagerProps> = ({
  mode,
  projectId,
}) => {
  const { data, isFetching, refetch } = useGetSiteLayoutQuery(projectId, {
    skip: projectId == null,
  });
  const [deleteLayout, { isLoading: deleting }] = useDeleteLayoutMutation();

  const layouts = useMemo(() => toCanvasLayouts(data), [data]);
  const devices = useMemo(() => toDevicePins(data), [data]);

  const [selectedLayoutId, setSelectedLayoutId] = useState<number | null>(null);
  const [overlayMode, setOverlayMode] = useState(false);
  const [addDeviceMode, setAddDeviceMode] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [pendingCoord, setPendingCoord] = useState<RealWorldCoordinate | null>(
    null,
  );
  const [hideWithinBounds, setHideWithinBounds] = useState(true);

  const isView = mode === "view";

  // In view mode, optionally hide devices whose status is within bounds (no alertStatus)
  const visibleDevices = useMemo(() => {
    if (!isView || !hideWithinBounds) return devices;
    return devices.filter((d) => d.alertStatus !== undefined);
  }, [devices, isView, hideWithinBounds]);

  // Resolve the active layout
  const activeLayout = useMemo(() => {
    if (layouts.length === 0) return null;
    const found = layouts.find((l) => l.id === selectedLayoutId);
    return found || layouts[0];
  }, [layouts, selectedLayoutId]);

  const transformer = useMemo(() => {
    if (!activeLayout || !activeLayout.isCalibrated) return null;
    try {
      return new CoordinateTransformer(activeLayout.calibrationPoints);
    } catch {
      return null;
    }
  }, [activeLayout]);

  const handleCanvasClick = (coord: Coordinate) => {
    if (!addDeviceMode || !transformer) return;
    setPendingCoord(transformer.imageToRealWorld(coord));
  };

  const handleRemoveLayout = async () => {
    if (!activeLayout?.id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove layout "${activeLayout.name}"?`)) return;
    try {
      await deleteLayout(activeLayout.id).unwrap();
      setSelectedLayoutId(null);
      refetch();
    } catch {
      // eslint-disable-next-line no-alert
      alert("Failed to remove layout.");
    }
  };

  const afterMutation = () => {
    refetch();
  };

  if (isFetching && !data) {
    return (
      <div className="text-muted" style={{ padding: 24 }}>
        Loading layout…
      </div>
    );
  }

  return (
    <div className={`sl-manager${addDeviceMode ? " sl-add-mode-active" : ""}`}>
      {addDeviceMode && (
        <div
          className="sl-add-mode-backdrop"
          onClick={() => setAddDeviceMode(false)}
        />
      )}
      <div className="sl-controls">
        {!overlayMode && layouts.length > 0 && (
          <select
            className="select sl-layout-select"
            value={activeLayout?.id ?? ""}
            onChange={(e) =>
              setSelectedLayoutId(
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
          >
            {layouts.map((l, i) => (
              <option key={l.id ?? `idx-${i}`} value={l.id ?? ""}>
                {l.name}
                {!l.isCalibrated ? " (not calibrated)" : ""}
              </option>
            ))}
          </select>
        )}

        {isView && (
          <button
            type="button"
            className={`btn btn-sm ${hideWithinBounds ? "btn-warning" : ""}`}
            title={
              hideWithinBounds
                ? "Currently showing abnormal checkpoints only — click to show all"
                : "Click to show abnormal checkpoints only"
            }
            onClick={() => setHideWithinBounds((v) => !v)}
          >
            <i
              className={`bi ${hideWithinBounds ? "bi-funnel-fill" : "bi-funnel"}`}
            />{" "}
            Show abnormal checkpoints only
          </button>
        )}

        {layouts.length > 1 && (
          <button
            type="button"
            className={`btn btn-sm ${overlayMode ? "btn-info" : ""}`}
            onClick={() => {
              setOverlayMode((v) => !v);
              setAddDeviceMode(false);
            }}
          >
            <i className="bi bi-layers" /> Overlay all
          </button>
        )}

        {!isView && (
          <>
            <button
              type="button"
              className="btn btn-sm btn-success"
              onClick={() => setShowWizard(true)}
            >
              <i className="bi bi-plus-lg" /> Add layout
            </button>
            {activeLayout?.id != null && !overlayMode && (
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={handleRemoveLayout}
                disabled={deleting}
              >
                <i className="bi bi-trash" /> Remove layout
              </button>
            )}
            {!overlayMode && activeLayout?.isCalibrated && (
              <button
                type="button"
                className={`btn btn-sm ${addDeviceMode ? "btn-warning" : ""}`}
                onClick={() => setAddDeviceMode((v) => !v)}
              >
                <i className="bi bi-geo-alt" />{" "}
                {addDeviceMode ? "Exit add-checkpoint mode" : "Add checkpoint"}
              </button>
            )}
          </>
        )}
      </div>

      {layouts.length === 0 ? (
        <div className="text-muted" style={{ padding: 24 }}>
          No layout uploaded yet.
          {!isView && " Use “Add layout” to upload and calibrate one."}
        </div>
      ) : overlayMode ? (
        <OverlayCanvas
          layouts={layouts}
          devices={visibleDevices}
          colorByStatus={isView}
        />
      ) : activeLayout ? (
        <div className="sl-canvas-container">
          <SiteLayoutCanvas
            imageUrl={activeLayout.imageUrl}
            calibrationPoints={activeLayout.calibrationPoints}
            devices={visibleDevices}
            isCalibrated={activeLayout.isCalibrated}
            transformer={transformer}
            isAddingMode={addDeviceMode}
            onImageClick={handleCanvasClick}
            colorByStatus={isView}
          />
          {addDeviceMode && (
            <div className="sl-add-mode-overlay">
              <div className="sl-add-mode-hint">
                <i className="bi bi-geo-alt-fill" style={{ fontSize: 22 }} />
                <span>Click anywhere on the map to place a new checkpoint</span>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {!isView && (
        <>
          <AddLayoutWizardModal
            show={showWizard}
            projectId={projectId}
            onHide={() => setShowWizard(false)}
            onLayoutCreated={afterMutation}
          />
          <DevicePlacementModal
            show={pendingCoord !== null}
            projectId={projectId}
            coordinate={pendingCoord}
            onClose={() => setPendingCoord(null)}
            onCreated={afterMutation}
          />
        </>
      )}
    </div>
  );
};

export default SiteLayoutManager;
